// src/pages/admin/FuncoesCapacidadeDia.js
import React, { useEffect, useMemo, useState } from "react";
import { useTable, useFilters, useSortBy } from "react-table";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";
import PageHeader from "pages/components/PageHeader";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import {
  CircularProgress,
  Card,
  Checkbox,
  FormControlLabel,
  TextField,
  Tooltip,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";

// API endpoints
const EP = {
  FUN_Sel: "Funcoes/SelFuncoes",
  FCD_SelMes: "FuncoesCapacidadeDia/SelMes",
  FCD_SetDia: "FuncoesCapacidadeDia/SetDia",
};

const toIntOrNull = (v) => {
  if (v === null || v === undefined) return null;
  if (typeof v === "string" && v.trim() === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// normaliza para comparar "Distribuição" / "Distribuicao"
const norm = (s) =>
  (s || "")
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

// --- Filter input (react-table)
function TextFilter({ column: { filterValue, setFilter } }) {
  return (
    <input
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      placeholder="filtrar…"
      style={{
        width: "100%",
        padding: "6px 4px",
        border: "none",
        borderBottom: "1px solid rgba(0,0,0,0.2)",
        outline: "none",
        background: "transparent",
        fontSize: 12,
        color: "#556",
      }}
      onFocus={(e) => (e.target.style.borderBottom = "1px solid rgba(0,0,0,0.35)")}
      onBlur={(e) => (e.target.style.borderBottom = "1px solid rgba(0,0,0,0.2)")}
    />
  );
}

// Persist filters
const FILTERS_KEY = "tbl:funcoescapacidade:filters";
const KEEP_KEY = "tbl:funcoescapacidade:keep";

export default function FuncoesCapacidadeDia() {
  const { showAlert } = useAlert();
  const { temPermissao, userData } = useAuth();
  const [loading, setLoading] = useState(false);

  const podeW = temPermissao("Menu Administração", "w");
  const podeU = temPermissao("Menu Administração", "u");

  const hoje = new Date();
  const [ano, setAno] = useState(hoje.getFullYear());
  const [mes, setMes] = useState(hoje.getMonth() + 1); // 1..12
  const [idFuncao, setIdFuncao] = useState("");

  const [funcoes, setFuncoes] = useState([]);
  const [dados, setDados] = useState([]);

  // Campo pequeno para aplicar no mês
  const [capTodos, setCapTodos] = useState(0);

  // Edições pendentes: { "YYYY-MM-DD": number }
  const [edits, setEdits] = useState({});

  // Manter filtros
  const initialKeep =
    (() => {
      try {
        const v = localStorage.getItem(KEEP_KEY);
        return v === null ? true : JSON.parse(v);
      } catch {
        return true;
      }
    })() ?? true;
  const [keepFilters, setKeepFilters] = useState(initialKeep);

  const initialFilters = keepFilters
    ? (() => {
        try {
          return JSON.parse(localStorage.getItem(FILTERS_KEY) || "[]");
        } catch {
          return [];
        }
      })()
    : [];

  // Carregar funções (default: Distribuição)
  async function carregarFuncoes() {
    try {
      const [rows] = await handleApi(api.post(EP.FUN_Sel));
      const list = (rows || [])
        .map((x) => ({ id: x.id, designacao: x.designacao }))
        .filter((x) => x.id && x.designacao);

      setFuncoes(list);

      // Se ainda não houver função selecionada, escolhe "Distribuição".
      if (!idFuncao) {
        const dist = list.find((f) => norm(f.designacao) === "distribuicao");
        setIdFuncao((dist || list[0] || {}).id || "");
      }
    } catch {
      setFuncoes([]);
    }
  }

  // Gera estrutura base dos dias do mês
  function diasMes(a, m) {
    const ultimo = new Date(a, m, 0).getDate();
    const arr = [];
    for (let d = 1; d <= ultimo; d++) {
      const dt = new Date(a, m - 1, d);
      const yyyy = dt.getFullYear();
      const mm = String(dt.getMonth() + 1).padStart(2, "0");
      const dd = String(dt.getDate()).padStart(2, "0");
      const iso = `${yyyy}-${mm}-${dd}`;
      const semana = ["dom.", "seg.", "ter.", "qua.", "qui.", "sex.", "sáb."][dt.getDay()];
      arr.push({ dia: iso, semana, capacidade: null, motivo: "" });
    }
    return arr;
  }

  async function carregar() {
    if (!idFuncao) return;
    setLoading(true);
    setEdits({});
    try {
      const pars = { ano: Number(ano), mes: Number(mes), idfuncao: Number(idFuncao) };
      const [rows] = await handleApi(api.post(EP.FCD_SelMes, pars));

      const byDia = {};
      (rows || []).forEach((r) => {
        const dia = String(r.dia || r.data || r.datadia || r.Data).slice(0, 10);
        byDia[dia] = {
          capacidade: Number(r.limitedia ?? 0),
          motivo: r.motivo || "",
        };
      });

      const base = diasMes(ano, mes).map((x) => ({
        ...x,
        capacidade: byDia[x.dia]?.capacidade ?? null,
        motivo: byDia[x.dia]?.motivo ?? "",
      }));
      setDados(base);
    } catch {
      showAlert("Falha ao carregar capacidades.", "error");
      setDados(diasMes(ano, mes));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    carregarFuncoes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ano, mes, idFuncao]);

  // grava 1 dia (com motivo)
  async function gravarDia(diaISO, capacidade, motivo = null) {
    if (!podeW && !podeU) return;

    const payload = {
      idfuncao: Number(idFuncao),
      dia: String(diaISO).slice(0, 10),
      limitedia: toIntOrNull(capacidade),
      created_by: Number(userData?.id || 0),
      motivo,
    };

    const [_, err, meta] = await handleApi(api.post(EP.FCD_SetDia, payload));
    if (err) return showAlert(err, "error");
    if (meta?.message) showAlert(meta.message, meta.severity || "success");
  }

  // aplica valor em todo o mês e faz refresh
  async function aplicarNoMes() {
    if (!idFuncao) return;
    setLoading(true);
    try {
      const cap = Number(capTodos) || 0;
      const dias = diasMes(ano, mes);
      const motivo = cap === 0 ? "Dia bloqueado (capacidade = 0)" : `Capacidade mensal = ${cap}`;
      for (const d of dias) {
        // eslint-disable-next-line no-await-in-loop
        await gravarDia(d.dia, cap, motivo);
      }
      await carregar();
      showAlert("Capacidade aplicada a todos os dias do mês.", "success");
    } finally {
      setLoading(false);
    }
  }

  // guarda apenas os edits pendentes (e envia motivo coerente)
  async function guardarAlteracoes() {
    const pendentes = Object.entries(edits); // [[dia, cap], ...]
    if (!pendentes.length) return;
    setLoading(true);
    try {
      for (const [dia, cap] of pendentes) {
        const n = Number(cap) || 0;
        const motivo =
          n === 0 ? "Dia bloqueado (capacidade = 0)" : `Capacidade especial definida: ${n}`;
        // eslint-disable-next-line no-await-in-loop
        await gravarDia(dia, n, motivo);
      }
      await carregar();
      showAlert("Alterações guardadas.", "success");
    } finally {
      setLoading(false);
    }
  }

  // --- Tabela
  const columns = useMemo(
    () => [
      {
        Header: "Dia",
        accessor: "dia",
        id: "dia",
        Filter: TextFilter,
        Cell: ({ value, row }) => (
          <div>
            <div style={{ fontWeight: 600 }}>{value}</div>
            <div style={{ fontSize: 12, color: "#7b809a" }}>{row.original.semana}</div>
          </div>
        ),
      },
      {
        Header: "Capacidade",
        accessor: (r) => (r.capacidade ?? "") + "",
        id: "capacidade",
        Filter: TextFilter,
        Cell: ({ row, value }) => {
          const dia = row.original.dia;
          const atual = edits[dia] !== undefined ? edits[dia] : value;
          return (
            <TextField
              type="number"
              size="small"
              value={atual}
              onChange={(e) =>
                setEdits((old) => ({
                  ...old,
                  [dia]: e.target.value,
                }))
              }
              inputProps={{ style: { width: 90 } }}
            />
          );
        },
      },
      {
        Header: "Motivo",
        accessor: (r) => r.motivo || "",
        id: "motivo",
        Filter: TextFilter,
        Cell: ({ value }) => (
          <div
            style={{
              maxWidth: 520,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {value}
          </div>
        ),
      },
    ],
    [edits]
  );

  const table = useTable(
    {
      columns,
      data: dados,
      initialState: { filters: initialFilters },
      autoResetFilters: false,
      autoResetSortBy: false,
    },
    useFilters,
    useSortBy
  );
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, setAllFilters, state } =
    table;

  // Persist filters
  useEffect(() => {
    try {
      localStorage.setItem(KEEP_KEY, JSON.stringify(keepFilters));
    } catch {}
    if (!keepFilters) {
      setAllFilters([]);
      try {
        localStorage.removeItem(FILTERS_KEY);
      } catch {}
    }
  }, [keepFilters, setAllFilters]);

  useEffect(() => {
    if (!keepFilters) return;
    try {
      localStorage.setItem(FILTERS_KEY, JSON.stringify(state.filters || []));
    } catch {}
  }, [state.filters, keepFilters]);

  const meses = [
    { v: 1, t: "Janeiro" },
    { v: 2, t: "Fevereiro" },
    { v: 3, t: "Março" },
    { v: 4, t: "Abril" },
    { v: 5, t: "Maio" },
    { v: 6, t: "Junho" },
    { v: 7, t: "Julho" },
    { v: 8, t: "Agosto" },
    { v: 9, t: "Setembro" },
    { v: 10, t: "Outubro" },
    { v: 11, t: "Novembro" },
    { v: 12, t: "Dezembro" },
  ];

  useEffect(() => {
    if (!idFuncao && funcoes.length) {
      const dist = funcoes.find((f) => norm(f.designacao) === "distribuicao");
      setIdFuncao((dist || funcoes[0]).id);
    }
  }, [funcoes, idFuncao]);

  const numEdits = Object.keys(edits).length;

  return (
    <DashboardLayout>
      <DashboardNavbar />

      {/* PROGRESS GLOBAL — sempre aqui, logo após o navbar */}
      {loading && (
        <MDBox py={2} display="flex" alignItems="center" justifyContent="center">
          <CircularProgress />
        </MDBox>
      )}
      <PageHeader />

      <MDBox pt={1}>
        <Card>
          <MDBox p={3}>
            <MDBox
              display="flex"
              flexWrap="wrap"
              gap={2}
              alignItems="flex-end"
              justifyContent="space-between"
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={keepFilters}
                    onChange={(e) => setKeepFilters(e.target.checked)}
                    size="small"
                  />
                }
                label="Manter filtros"
              />

              <MDBox flex="1 1 180px" minWidth={160}>
                <label>Função</label>
                <select
                  value={idFuncao}
                  onChange={(e) => setIdFuncao(e.target.value)}
                  style={{
                    width: "100%",
                    padding: 12,
                    borderRadius: 6,
                    border: "1px solid #ced4da",
                    fontSize: 14,
                  }}
                >
                  {funcoes.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.designacao}
                    </option>
                  ))}
                </select>
              </MDBox>

              <MDBox flex="1 1 100px" minWidth={100}>
                <label>Ano</label>
                <TextField
                  type="number"
                  fullWidth
                  value={ano}
                  onChange={(e) => setAno(Number(e.target.value || hoje.getFullYear()))}
                />
              </MDBox>

              <MDBox flex="1 1 150px" minWidth={140}>
                <label>Mês</label>
                <select
                  value={mes}
                  onChange={(e) => setMes(Number(e.target.value))}
                  style={{
                    width: "100%",
                    padding: 12,
                    borderRadius: 6,
                    border: "1px solid #ced4da",
                    fontSize: 14,
                  }}
                >
                  {meses.map((m) => (
                    <option key={m.v} value={m.v}>
                      {m.t}
                    </option>
                  ))}
                </select>
              </MDBox>

              <MDBox flex="1 1 140px" minWidth={120}>
                <TextField
                  type="number"
                  value={capTodos}
                  onChange={(e) => setCapTodos(Number(e.target.value || 0))}
                  placeholder="cap."
                  inputProps={{ style: { width: "100%" } }}
                />
                <Tooltip title="Aplicar o valor a todos os dias do mês">
                  <span>
                    <MDButton
                      color="warning"
                      onClick={aplicarNoMes}
                      disabled={loading || !idFuncao}
                      sx={{ px: 2, minWidth: 0, whiteSpace: "nowrap", mt: 0.5 }}
                    >
                      Aplicar no Mês
                    </MDButton>
                  </span>
                </Tooltip>
              </MDBox>
            </MDBox>

            <MDBox mt={1} display="flex" gap={12} alignItems="center">
              <Tooltip
                title={
                  numEdits
                    ? `Guardar ${numEdits} alteração(ões) pendente(s)`
                    : "Sem alterações pendentes"
                }
              >
                <span>
                  <MDButton
                    color="success"
                    onClick={guardarAlteracoes}
                    startIcon={<SaveIcon />}
                    disabled={loading || numEdits === 0}
                    sx={{ px: 2, minWidth: 0, whiteSpace: "nowrap" }}
                  >
                    Guardar alterações {numEdits ? `(${numEdits})` : ""}
                  </MDButton>
                </span>
              </Tooltip>
            </MDBox>

            <MDBox mt={3} sx={{ height: "68vh", overflow: "auto", borderRadius: 1 }}>
              <table {...getTableProps()} style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  {headerGroups.map((hg) => (
                    <tr {...hg.getHeaderGroupProps()}>
                      {hg.headers.map((col) => (
                        <th
                          {...col.getHeaderProps(col.getSortByToggleProps())}
                          style={{
                            position: "sticky",
                            top: 0,
                            zIndex: 2,
                            background: "#fff",
                            textAlign: "left",
                            padding: "6px 8px",
                            fontSize: 12,
                            color: "#7b809a",
                            verticalAlign: "bottom",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center" }}>
                            {col.render("Header")}
                            {col.isSorted ? (
                              col.isSortedDesc ? (
                                <span style={{ marginLeft: 6 }}>▼</span>
                              ) : (
                                <span style={{ marginLeft: 6 }}>▲</span>
                              )
                            ) : null}
                          </div>
                          {!col.disableFilters && (
                            <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 4 }}>
                              {col.canFilter ? col.render("Filter") : null}
                            </div>
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {rows.map((row) => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()} style={{ borderBottom: "1px solid #f0f2f5" }}>
                        {row.cells.map((cell) => (
                          <td
                            {...cell.getCellProps()}
                            style={{
                              whiteSpace: "nowrap",
                              padding: "10px 8px",
                              fontSize: 12,
                              color: "#344767",
                            }}
                          >
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}
